import React from 'react'
import { Checkbox, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import styles from './style.module.scss'

import classNames from 'classnames'
import { renderHighlightedText } from 'utils/ui'

import imgRecurrence from 'assets/images/calendar/recurrence-16.svg'
import imgDot from 'assets/images/todo/icon-dot.svg'
import imgAccessoryDue from 'assets/images/todo/icon-due.svg'
import imgButtonImportantChecked from 'assets/images/todo/icon-important-checked.svg'
import imgButtonImportant from 'assets/images/todo/icon-important.svg'
import imgNote from 'assets/images/todo/icon-notes.svg'
import imgReminderIcon from 'assets/images/todo/icon-reminder.svg'
import { getReminderTime, TodoItem } from 'pages/todo/modules/TodoItem'
import { useTranslation } from 'react-i18next'
import { updateTodoDueDate, updateTodoRecurrence } from '../../../modules/TodoUpdater'
import {
  formatDueDate,
  getReminderTimeStr,
  isOverdue,
  isOverdueReminder,
} from '../../DateFormatter'
import useDueDateRecurrence from '../../hooks/useDueDateRecurrence'
import { createReminderOptions } from '../../TodoEditor/ReminderSelector'
import { useCheckboxHandler } from './hooks/useCheckboxHandler'
import { useReminderHandler } from './hooks/useReminderHandler'
import { createClickHandler } from './utils/eventHandlers'

interface TodoListItemProps {
  summary: string
  folderName?: string
  dueDate: Date | string | null
  startDate?: Date | string | null
  reminders?: ReminderAlarm[]
  description?: string
  recurrenceRule?: any
  important: boolean
  completed: boolean
  onClick: () => void
  folderColor?: string
  isSelected?: boolean
  onImportantClick: () => void
  onCompleteToggle: (checked: boolean) => void
  taskInstance: any
  hideSubInfo?: boolean
  onTodoUpdate?: () => void
  isReadOnly?: boolean
  onOpenDatePicker?: (
    type: string,
    date: Date,
    taskInstance: TodoItem,
    callback: (date: Date) => void,
  ) => void
  onOpenRecurrenceEditor?: (
    rrule: any,
    startDate: Date,
    options: any,
    callback: (options: any, startDate: Date) => void,
  ) => void
  searchText?: string
  timeFormat?: string
  dateFormat?: string
}

type ReminderAlarm =
  | {
      action: string
      end?: Date
      trigger: string
    }
  | {
      action: string
      end?: Date
      trigger: {
        params: Record<string, any>
        val: string
      }
      uid?: string
    }

interface ReminderOption {
  key: string
  label: string
  value: Date | string | null
  isDateTime: boolean
}

const TodoListItem: React.FC<TodoListItemProps> = ({
  summary,
  folderName,
  dueDate,
  startDate,
  reminders,
  description,
  recurrenceRule,
  important,
  completed,
  onClick,
  folderColor = '#fd4c45',
  isSelected: _isSelected = false,
  onImportantClick,
  onCompleteToggle,
  taskInstance,
  hideSubInfo,
  onTodoUpdate, // 새로 추가: todo 업데이트 콜백
  isReadOnly = false, // 새로 추가: 읽기 전용 모드
  onOpenDatePicker, // Date picker를 열기 위한 콜백
  onOpenRecurrenceEditor, // Recurrence editor를 열기 위한 콜백
  searchText = '', // 검색 텍스트 하이라이트를 위한 prop
  timeFormat: _timeFormat, // 시간 포맷 설정 (리렌더링 트리거용)
  dateFormat: _dateFormat, // 날짜 포맷 설정 (리렌더링 트리거용)
}) => {
  const { t } = useTranslation()

  // 체크박스 처리 훅
  const { localCompleted, handleCheckboxChange } = useCheckboxHandler({
    completed,
    onCompleteToggle,
  })

  // 리마인더 처리 훅
  const {
    reminderDropdownOpen,
    setReminderDropdownOpen,
    handleReminderClick,
    handleReminderChange,
    handleReminderPickDateClick,
  } = useReminderHandler({
    taskInstance,
    isReadOnly,
    startDate: startDate ? (typeof startDate === 'string' ? new Date(startDate) : startDate) : null,
    onTodoUpdate,
    onOpenDatePicker,
  })

  // Use the common hook for due date and recurrence functionality
  const { dueDateDropdownOpen, setDueDateDropdownOpen, createDueDateDropdownMenuItems } =
    useDueDateRecurrence({
      dueDate: dueDate ? (typeof dueDate === 'string' ? new Date(dueDate) : dueDate) : null,
      rrule: recurrenceRule ? { recurrenceRule: recurrenceRule } : null,
      updateHandlers: {
        updateDueDate: (date: Date | null, callback?: (success: boolean) => void) => {
          if (taskInstance && !isReadOnly) {
            // Clear recurrence when manually picking a date
            if (recurrenceRule) {
              updateTodoRecurrence(taskInstance, null, date, callback)
            } else {
              updateTodoDueDate(taskInstance, date, callback)
            }
          }
        },
        updateRecurrence: (rrule: any, startDate: Date, callback?: (success: boolean) => void) => {
          if (taskInstance && !isReadOnly) {
            updateTodoRecurrence(taskInstance, rrule, startDate, callback)
          }
        },
      },
      onTodoListUpdate: onTodoUpdate,
      onOpenDatePicker,
      onOpenRecurrenceEditor,
    })

  // Due date handlers
  const handleDueDateClick = createClickHandler(() => setDueDateDropdownOpen(true), isReadOnly)

  // Due date dropdown items from hook
  const dueDateDropdownItems: any = createDueDateDropdownMenuItems(styles, true)

  const hasExistingReminder = reminders && reminders.length > 0
  let reminderTime: Date | null = null
  if (hasExistingReminder) {
    reminderTime = getReminderTime(
      reminders[0],
      dueDate ? (typeof dueDate === 'string' ? dueDate : dueDate.toISOString()) : undefined,
      startDate ? (typeof startDate === 'string' ? new Date(startDate) : startDate) : undefined,
    )
  }

  const reminderDropdownItems: MenuProps['items'] = createReminderOptions(
    t,
    hasExistingReminder,
    true,
  ).map((option: ReminderOption) => ({
    key: option.key,
    label: (
      <div className={styles.menuItem}>
        <div className={styles.menuItemContent}>
          <div className={styles.reminderInfo}>
            <div className={styles.reminderLabel}>{option.label}</div>
          </div>
        </div>
      </div>
    ),
    onClick: (e: any) => {
      if (isReadOnly) return
      e.domEvent.stopPropagation()
      if (option.key === 'pick_date') {
        handleReminderPickDateClick()
      } else if (option.key === 'off') {
        handleReminderChange(null, false)
      } else {
        handleReminderChange(option.value as Date | string, option.isDateTime)
      }
    },
    disabled: isReadOnly,
  }))

  return (
    <div className={[styles.todoItem, completed ? styles.checked : ''].join(' ')} onClick={onClick}>
      {/* Complete Checkbox */}
      <div className={styles.checkBoxArea} onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={localCompleted}
          disabled={isReadOnly}
          onChange={handleCheckboxChange}
          className={styles.checkBox}
          style={{ '--checkbox-color': folderColor } as React.CSSProperties}
        />
      </div>

      {/* 제목, Folder 이름, 날짜, 알림 시간 */}
      <div className={styles.infoArea}>
        {/* 제목 */}
        <div className={hideSubInfo ? styles.onlySummary : styles.summary}>
          {summary ? renderHighlightedText(summary, searchText) : t('todo.detail.noSubject')}
        </div>

        {/* Folder 이름, 날짜, 알림 시간 */}
        {!hideSubInfo && (
          <div className={styles.todoInfoLayout}>
            {/* Folder 이름 */}
            {folderName && <span className={styles.folderName}>{folderName}</span>}
            {/* 날짜 */}
            {/* due 날짜 */}
            {dueDate && (
              <>
                <span className={styles.dot}>
                  <img src={imgDot} alt="dot" />
                </span>

                <Dropdown
                  menu={{ items: dueDateDropdownItems }}
                  trigger={isReadOnly ? [] : ['click']}
                  open={dueDateDropdownOpen}
                  onOpenChange={setDueDateDropdownOpen}
                  placement="bottomLeft"
                  overlayClassName={classNames('contextMenu', styles.datePickerDropdown)}
                  getPopupContainer={() => document.body}
                >
                  <span
                    className={`${dueDate ? styles.selectorActive : styles.selectorNone}`}
                    style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
                    onClick={handleDueDateClick}
                  >
                    <img src={imgAccessoryDue} alt="due" className={styles.iconSmall} />
                    <span
                      className={[
                        styles.dueText,
                        isOverdue(typeof dueDate === 'string' ? new Date(dueDate) : dueDate)
                          ? styles.overdueDueText
                          : '',
                      ].join(' ')}
                    >
                      {formatDueDate(typeof dueDate === 'string' ? new Date(dueDate) : dueDate, t)}
                    </span>
                  </span>
                </Dropdown>
              </>
            )}
            {/* 알림 시간 */}
            {reminders?.length > 0 && (
              <>
                <span className={styles.dot}>
                  <img src={imgDot} alt="dot" />
                </span>

                <Dropdown
                  menu={{ items: reminderDropdownItems }}
                  trigger={isReadOnly ? [] : ['click']}
                  open={reminderDropdownOpen}
                  onOpenChange={setReminderDropdownOpen}
                  placement="bottomLeft"
                  overlayClassName="contextMenu"
                  getPopupContainer={() => document.body}
                >
                  <span
                    className={`${reminders[0] ? styles.selectorActive : styles.selectorNone}`}
                    onClick={handleReminderClick}
                    style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
                  >
                    <img src={imgReminderIcon} alt="reminder" className={styles.iconSmall} />

                    <span
                      className={[
                        styles.dueText,
                        isOverdueReminder(reminderTime) ? styles.overdueDueText : '',
                      ].join(' ')}
                    >
                      {getReminderTimeStr(reminders[0], dueDate, startDate, t)}
                    </span>
                  </span>
                </Dropdown>
              </>
            )}
            {/* description */}
            {description && (
              <>
                <span className={styles.dot}>
                  <img src={imgDot} alt="dot" />
                </span>

                <span className={styles.reminder}>
                  <img src={imgNote} alt="note" className={styles.iconSmall} />
                </span>
              </>
            )}
            {/* recurrence */}
            {recurrenceRule && (
              <>
                <span className={styles.dot}>
                  <img src={imgDot} alt="dot" />
                </span>

                <span className={styles.reminder}>
                  <img src={imgRecurrence} alt="recurrence" className={styles.iconSmall} />
                </span>
              </>
            )}
          </div>
        )}
      </div>
      <div className={styles.accessoryArea}>
        <div
          className={classNames(styles.importantBtn)}
          onClick={createClickHandler(() => {
            if (typeof important !== 'undefined' && typeof onImportantClick === 'function') {
              onImportantClick()
            }
          }, isReadOnly)}
          style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
        >
          {/* 아이콘 ???*/}
          <img
            src={important ? imgButtonImportantChecked : imgButtonImportant}
            alt={important ? 'important-checked' : 'important'}
          />
        </div>
      </div>
    </div>
  )
}

export default TodoListItem
